import { JsonObject, UiOption } from '../../helpers';
import { CreateBaseModel } from '../base.model';
import { CreateTelemetryModel, PatchTelemetryModel, TelemetryModel } from './telemetry.model';
import { object, string } from 'yup';
import HttpsIcon from '@/assets/telemetry-icons/https.png';

export type HttpTelemetryConfig = {
  url: string;
  authType: 'bearer' | 'api_key' | 'none';
  authHeaderName: string; // Only used when `authType` is `api_key`, default is X-API-Key
  authToken: string;
  authTokenLast4: string;
  allowSelfSignedCertificates: boolean;
  format: 'ndjson' | 'json-array';
  compress: boolean;
};

export class HttpTelemetryModel extends TelemetryModel {
  config: HttpTelemetryConfig;

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof TelemetryModel, unknown>;

    this.type = 'http';
    this.typeLabel = 'HTTPS Endpoint';
    this.iconImage = HttpsIcon;

    this.config = (data.config as HttpTelemetryConfig) || {
      url: '',
      authType: 'none',
      authHeaderName: '',
      authToken: '',
      authTokenLast4: '',
      allowSelfSignedCertificates: false,
      format: 'ndjson',
      compress: true,
    };

    if (!this.config.authType) {
      this.config.authType = 'none';
    }

    this.schema = this.schema.concat(
      object({
        config: object({
          url: string()
            .matches(/^https:\/\//, 'Endpoint URL must start with https://')
            .required('Endpoint URL is required'),
        }),
      })
    );
  }

  get authDisabled() {
    return this.config.authType === 'none';
  }

  get usesAuthHeader() {
    return this.config.authType === 'api_key';
  }

  get createModel(): CreateBaseModel {
    return new CreateHttpTelemetryModel(this);
  }

  get patchModel(): CreateBaseModel {
    return new PatchHttpTelemetryModel(this);
  }
}

class CreateHttpTelemetryModel extends CreateTelemetryModel {
  config: Partial<HttpTelemetryConfig>;

  constructor(telemetry: HttpTelemetryModel) {
    super(telemetry);

    this.type = 'http';
    this.config = telemetry.config;

    delete this.connectorId;
    delete this.config.authTokenLast4;

    if (this.config.authType === 'bearer') {
      this.config.authHeaderName = 'Authorization';
    }

    if (!this.config.authType || (this.config.authType as string) === 'none') {
      delete this.config.authType;
      delete this.config.authHeaderName;
      delete this.config.authToken;
    }
  }
}

type PatchHttpTelemetryConfig = Omit<Partial<HttpTelemetryConfig>, 'authType'> & {
  authType?: 'bearer' | 'api_key' | 'none' | null;
};

class PatchHttpTelemetryModel extends PatchTelemetryModel {
  config: PatchHttpTelemetryConfig;

  constructor(telemetry: HttpTelemetryModel) {
    super(telemetry);

    this.config = telemetry.config;

    delete this.connectorId;
    delete this.config.authTokenLast4;

    if (!this.config.authToken) {
      delete this.config.authToken;
    }

    if (this.config.authType === 'bearer') {
      this.config.authHeaderName = 'Authorization';
    }

    if (this.config.authType === 'none') {
      this.config.authType = null;
      delete this.config.authHeaderName;
      delete this.config.authToken;
    }
  }
}

export const HttpTelemetryFormatOpts: UiOption[] = [
  { label: 'Newline-delimited JSON (NDJSON)', value: 'ndjson' },
  { label: 'JSON array', value: 'json-array' },
];
